/**
 * Sabotage Report — what was placed, what fired, and on whom.
 *
 * Placements and firings are shown separately because they are not one-to-one:
 * one placement fires once per victim team that reaches the planet, and two
 * teams can bury a sabotage on the SAME planet (in production game B8JS, both
 * Planet 4 and Planet 8 held two Treasure Thieves, each firing on every
 * arrival). A placement that never fired is worth seeing too.
 */

import { TrlTreasureRows } from '@swtorbit/shared-components';
import type { TrlSabotageStats } from '@swtorbit/shared-socket-events';
import { resolveServerUrl } from '../../../utils/serverUrl';
import { StatCard } from './StatCard';
import { formatClock, humanizeSlug } from './statsFormat';

export function TrlSabotageCard({ sabotage }: { sabotage: TrlSabotageStats }) {
  const { placements, firings } = sabotage;

  return (
    <StatCard
      title="Sabotage Report"
      icon="💣"
      summary={`${placements.length} placed · ${firings.length} fired`}
      isEmpty={placements.length === 0 && firings.length === 0}
      emptyMessage="No sabotage was placed in this game."
    >
      <div className="d-flex flex-column gap-sm">
        <div className="d-flex flex-column gap-2xs">
          <span className="fs-xs text-slate">Placed before the game</span>
          {placements.map((p) => (
            <div key={`${p.byTeamId}:${p.nodeId}`} className="d-flex gap-xs align-items-center fs-sm flex-wrap">
              <span className="text-white flex-1" style={{ minWidth: '9rem' }}>{p.byTeamName}</span>
              <span className="text-cyan" style={{ minWidth: '8rem' }}>{humanizeSlug(p.type)}</span>
              <span className="text-cyan" style={{ minWidth: '6rem' }}>{p.nodeLabel}</span>
              <span className={p.timesFired > 0 ? 'text-gold fs-xs' : 'text-slate fs-xs'}>
                {p.timesFired > 0 ? `fired ${p.timesFired}×` : 'never fired'}
              </span>
            </div>
          ))}
        </div>

        {firings.length > 0 && (
          <div className="d-flex flex-column gap-xs">
            <span className="fs-xs text-slate">Firings</span>
            {firings.map((f, idx) => (
              <div key={`${f.saboteurTeamId}:${f.victimTeamId}:${f.firedAtUtcMs}:${idx}`} className="d-flex flex-column gap-2xs">
                <div className="d-flex gap-xs align-items-center fs-sm flex-wrap">
                  <span className="text-slate fs-xs" style={{ minWidth: '5rem' }}>
                    {formatClock(f.firedAtUtcMs)}
                  </span>
                  <span className="text-white">{f.saboteurTeamName}</span>
                  <span className="text-slate">→</span>
                  <span className="text-white">{f.victimTeamName}</span>
                  <span className="text-cyan fs-xs">
                    {humanizeSlug(f.type)} · {f.nodeLabel}
                  </span>
                </div>
                {f.stolen.length > 0 && (
                  <div className="ps-sm">
                    <TrlTreasureRows
                      rows={f.stolen.map((row) =>
                        row.icon.kind === 'cartel' && row.icon.url
                          ? { ...row, icon: { ...row.icon, url: resolveServerUrl(row.icon.url) } }
                          : row,
                      )}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </StatCard>
  );
}
